import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "../../lib/supabaseClient";

const Sleepgoal = () => {
  const TARGET_HOURS = 8;
  const [sleep, setSleep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchSleep = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }
        setUserId(user.id);
        const { data } = await supabase.from("daily_activity").select("sleep").eq("user_id", user.id).eq("date", today).maybeSingle();
        if (data) setSleep(data.sleep || 0);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    fetchSleep();
  }, [today]);

  const updateSleep = async (delta) => {
  if (!userId) return;

  // 1. Calculate the new value first
  const newSleep = Math.max(0, Math.min(10, +(sleep + delta).toFixed(1)));

  // 2. Update the UI immediately (Optimistic Update)
  setSleep(newSleep);

  // 3. Update the Backend
  try {
    const { error } = await supabase
      .from("daily_activity")
      .upsert(
        { user_id: userId, date: today, sleep: newSleep }, 
        { onConflict: "user_id,date" }
      );

    if (error) throw error;
  } catch (err) {
    console.error("Error updating sleep:", err.message);
    // Optional: Revert state if the DB call fails
    // setSleep(sleep); 
  }
};

  const progress = Math.min((sleep / TARGET_HOURS) * 100, 100);
  const isGoalMet = sleep >= TARGET_HOURS;

  if (loading) return <div style={styles.loader}>Creating a quiet space...</div>;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Top Navigation Style Header */}
        <div style={styles.nav}>
          <div style={styles.dot} />
          <span style={styles.navDate}>{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          <div style={styles.dot} />
        </div>

        <header style={styles.header}>
          <h2 style={styles.title}>Sleep <span style={styles.titleThin}>Insights</span></h2>
          <p style={styles.subtitle}>Log your recovery cycles</p>
        </header>

        {/* The Glass Orb Card */}
        <section style={{...styles.glassCard, boxShadow: isGoalMet ? "0 0 50px rgba(129, 140, 248, 0.2)" : "0 20px 40px rgba(0,0,0,0.4)"}}>
          <div style={styles.orbContainer}>
            <svg width="220" height="220" viewBox="0 0 100 100">
              {/* Background Track */}
              <circle cx="50" cy="50" r="44" stroke="rgba(255,255,255,0.05)" strokeWidth="4" fill="none" />
              {/* Progress Glow */}
              <circle
                cx="50" cy="50" r="44"
                stroke="url(#purpleGlow)" strokeWidth="6" fill="none"
                strokeDasharray="276.46"
                style={{ 
                    strokeDashoffset: 276.46 - (progress / 100) * 276.46, 
                    transition: "stroke-dashoffset 1.5s cubic-bezier(0.22, 1, 0.36, 1)",
                    filter: "drop-shadow(0 0 8px #818cf8)"
                }}
                strokeLinecap="round" transform="rotate(-90 50 50)"
              />
              <defs>
                <linearGradient id="purpleGlow" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#a855f7" />
                </linearGradient>
              </defs>
            </svg>
            
            <div style={styles.centerText}>
              <span style={styles.mainNum}>{sleep}</span>
              <span style={styles.subNum}>hrs</span>
            </div>
          </div>

          <div style={styles.statsRow}>
            <div style={styles.miniStat}>
              <span style={styles.miniLabel}>TARGET</span>
              <span style={styles.miniVal}>{TARGET_HOURS}h</span>
            </div>
            <div style={styles.miniDivider} />
            <div style={styles.miniStat}>
              <span style={styles.miniLabel}>DEBT</span>
              <span style={styles.miniVal}>{Math.max(0, TARGET_HOURS - sleep)}h</span>
            </div>
          </div>
        </section>

        {/* Smooth Interaction Controls */}
        <div style={styles.actionGrid}>
          <button style={styles.glassBtn} onClick={() => updateSleep(-0.5)}>
            <span style={{opacity: 0.6}}>− 30m</span>
          </button>
          <button style={styles.glassBtn} onClick={() => updateSleep(0.5)}>
            <span style={{opacity: 0.6}}>+ 30m</span>
          </button>
        </div>

        <button 
            style={{...styles.primaryBtn, background: isGoalMet ? "linear-gradient(90deg, #4f46e5, #7c3aed)" : "#ffffff10"}} 
            onClick={() => updateSleep(1)}
        >
          Quick Add 1 Hour
        </button>

        {/* AI-Style Insight */}
        <div style={styles.aiInsight}>
          <div style={styles.aiIcon}>✦</div>
          <p style={styles.aiText}>
            {isGoalMet 
              ? "Optimization complete. Your nervous system is primed for peak performance today." 
              : "Logging consistent rest periods will improve your cognitive baseline by 15%."}
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: "100vh", background: "#05070a", color: "#e2e8f0", padding: "40px 20px", fontFamily: "'Inter', sans-serif" },
  container: { maxWidth: "400px", margin: "0 auto" },
  loader: { textAlign: "center", marginTop: "100px", color: "#6366f1", letterSpacing: "2px" },
  
  nav: { display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginBottom: "40px" },
  dot: { width: "4px", height: "4px", background: "#334155", borderRadius: "50%" },
  navDate: { fontSize: "12px", fontWeight: "600", color: "#64748b", textTransform: "uppercase", letterSpacing: "1px" },

  header: { marginBottom: "30px", textAlign: "left" },
  title: { fontSize: "32px", fontWeight: "800", margin: 0, color: "#fff" },
  titleThin: { fontWeight: "200", color: "#94a3b8" },
  subtitle: { color: "#475569", fontSize: "15px", marginTop: "4px" },

  glassCard: { 
    background: "rgba(255, 255, 255, 0.03)", backdropFilter: "blur(12px)",
    borderRadius: "40px", padding: "40px 20px", textAlign: "center",
    border: "1px solid rgba(255, 255, 255, 0.08)", position: "relative"
  },
  orbContainer: { position: "relative", display: "inline-block", marginBottom: "30px" },
  centerText: { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
  mainNum: { fontSize: "64px", fontWeight: "200", color: "#fff", display: "block", lineHeight: "0.8" },
  subNum: { fontSize: "14px", fontWeight: "600", color: "#6366f1", textTransform: "uppercase" },

  statsRow: { display: "flex", justifyContent: "center", gap: "40px", marginTop: "10px" },
  miniStat: { display: "flex", flexDirection: "column", gap: "4px" },
  miniLabel: { fontSize: "10px", fontWeight: "800", color: "#475569", letterSpacing: "1px" },
  miniVal: { fontSize: "16px", fontWeight: "600", color: "#f8fafc" },
  miniDivider: { width: "1px", background: "rgba(255,255,255,0.1)", height: "30px" },

  actionGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginTop: "30px" },
  glassBtn: { 
    height: "54px", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.05)",
    background: "rgba(255,255,255,0.02)", color: "#fff", cursor: "pointer", fontWeight: "600"
  },
  primaryBtn: {
    width: "100%", height: "60px", borderRadius: "20px", border: "none", marginTop: "15px",
    color: "#fff", fontWeight: "700", cursor: "pointer", transition: "all 0.3s ease"
  },

  aiInsight: { 
    marginTop: "40px", padding: "24px", background: "rgba(99, 102, 241, 0.05)", 
    borderRadius: "24px", border: "1px solid rgba(99, 102, 241, 0.1)", display: "flex", gap: "15px" 
  },
  aiIcon: { color: "#818cf8", fontSize: "18px", fontWeight: "bold" },
  aiText: { margin: 0, fontSize: "13px", color: "#94a3b8", lineHeight: "1.6" }
};

export default Sleepgoal;